import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CircleAlert,
  MessageSquareText,
  PhoneCall,
  RefreshCw,
  Send,
  Users,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import fetchData from "../api/fetchApi";
import { getLang } from "../i18n";

function clamp(value) {
  return Math.max(0, Math.min(Number.isFinite(value) ? value : 0, 100));
}

function daysUntil(date) {
  if (!date) return 0;
  return Math.max(Math.ceil((new Date(date) - new Date()) / 86400000), 0);
}

function DashboardSkeleton() {
  return (
    <div className="dashboard-stack" aria-label="Loading dashboard">
      <div className="skeleton h-28 rounded-2xl" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <div key={item} className="skeleton h-36 rounded-2xl" />)}
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="skeleton h-72 rounded-2xl" />
        <div className="skeleton h-72 rounded-2xl" />
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const isArabic = getLang() === "ar";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchData("dashboard"));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const number = useMemo(
    () => new Intl.NumberFormat(isArabic ? "ar-LY" : "en-US"),
    [isArabic]
  );

  if (loading) return <DashboardSkeleton />;

  if (error) {
    return (
      <section className="empty-state">
        <span className="empty-state-icon"><CircleAlert size={25} /></span>
        <h2>{isArabic ? "تعذر تحميل لوحة التحكم" : "Dashboard unavailable"}</h2>
        <p>{error}</p>
        <button type="button" className="secondary-button" onClick={loadDashboard}><RefreshCw size={17} />{isArabic ? "إعادة المحاولة" : "Try again"}</button>
      </section>
    );
  }

  const quota = data?.quota || {};
  const session = data?.session || {};
  const dashboard = data?.dashboard_data || {};
  const isTotalLimit = quota.limit === "total";
  const used = Number(isTotalLimit ? quota.this_month_used : quota.daily_used) || 0;
  const limit = Number(isTotalLimit ? quota.total_limit : quota.daily_limit) || 0;
  const usagePercent = clamp(limit ? (used / limit) * 100 : 0);
  const remaining = Math.max(limit - used, 0);
  const daysLeft = daysUntil(quota.expiration_date);
  const connected = String(session.status).toLowerCase() === "connected";
  const ArrowIcon = isArabic ? ArrowLeft : ArrowRight;

  const stats = [
    { label: isArabic ? "العملاء" : "Customers", value: dashboard.clients || 0, icon: Users, tone: "teal" },
    { label: isArabic ? "المجموعات" : "Groups", value: dashboard.client_groups || 0, icon: UsersRound, tone: "blue" },
    { label: isArabic ? "رسائل اليوم" : "Messages today", value: quota.daily_used || 0, icon: Send, tone: "amber" },
    { label: isArabic ? "إجمالي الرسائل" : "Total messages", value: quota.total_used || 0, icon: MessageSquareText, tone: "slate" },
  ];

  return (
    <div className="dashboard-stack">
      <section className="dashboard-hero">
        <div>
          <span className="eyebrow eyebrow-light">{isArabic ? "حالة الحساب الآن" : "Account status now"}</span>
          <h2>{isArabic ? "كل ما تحتاجه لإدارة محادثاتك" : "Everything you need to run your conversations"}</h2>
          <p>{isArabic ? "راقب الجلسة والاستهلاك، ثم انتقل مباشرة إلى الإجراء التالي." : "Monitor your connection and usage, then move directly to the next task."}</p>
        </div>
        <div className="hero-actions">
          <Link to="/admin/send-message" className="hero-primary"><Send size={18} />{isArabic ? "إرسال رسالة" : "Send message"}</Link>
          <Link to="/admin/session" className="hero-secondary"><PhoneCall size={18} />{isArabic ? "إدارة الربط" : "Manage connection"}</Link>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label={isArabic ? "مؤشرات الحساب" : "Account metrics"}>
        {stats.map(({ label, value, icon: Icon, tone }) => (
          <article className="metric-card" key={label}>
            <span className={`metric-icon metric-${tone}`}><Icon size={21} /></span>
            <span className="metric-label">{label}</span>
            <strong>{number.format(value)}</strong>
          </article>
        ))}
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.25fr_0.75fr]">
        <article className="panel-card">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">{isArabic ? "الاستهلاك" : "Usage"}</span>
              <h3>{isTotalLimit ? (isArabic ? "حد الرسائل الشهري" : "Monthly message limit") : (isArabic ? "حد الرسائل اليومي" : "Daily message limit")}</h3>
            </div>
            <span className="panel-value">{number.format(Math.round(usagePercent))}%</span>
          </div>
          <div className="usage-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Math.round(usagePercent)}>
            <span style={{ width: `${usagePercent}%` }} />
          </div>
          <div className="usage-details">
            <div><span>{isArabic ? "المستخدم" : "Used"}</span><strong>{number.format(used)}</strong></div>
            <div><span>{isArabic ? "المتبقي" : "Remaining"}</span><strong>{number.format(remaining)}</strong></div>
            <div><span>{isArabic ? "الحد" : "Limit"}</span><strong>{number.format(limit)}</strong></div>
          </div>
          {limit === 0 && <p className="panel-note"><CircleAlert size={16} />{isArabic ? "لا توجد حصة رسائل نشطة على الحساب حالياً." : "There is no active message quota on this account."}</p>}
        </article>

        <article className="panel-card connection-card">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">{isArabic ? "واتساب" : "WhatsApp"}</span>
              <h3>{isArabic ? "حالة الجلسة" : "Session status"}</h3>
            </div>
            <span className={`status-dot ${connected ? "status-online" : "status-offline"}`} />
          </div>
          <strong className="connection-status">{connected ? (isArabic ? "متصلة" : "Connected") : (isArabic ? "غير متصلة" : "Disconnected")}</strong>
          <p>{session.name || (isArabic ? "لم يتم العثور على اسم للجلسة" : "No session name available")}</p>
          <Link to="/admin/session" className="panel-link">{connected ? (isArabic ? "عرض إعدادات الجلسة" : "View session settings") : (isArabic ? "ربط واتساب الآن" : "Connect WhatsApp now")}<ArrowIcon size={17} /></Link>
        </article>
      </section>

      <section className="panel-card subscription-card">
        <span className="metric-icon metric-teal"><CalendarClock size={21} /></span>
        <div>
          <span className="eyebrow">{isArabic ? "الاشتراك" : "Subscription"}</span>
          <h3>{isArabic ? `${number.format(daysLeft)} يوم متبقٍ` : `${number.format(daysLeft)} days remaining`}</h3>
          <p>{quota.expiration_date ? (isArabic ? `ينتهي في ${new Intl.DateTimeFormat("ar-LY", { dateStyle: "long" }).format(new Date(quota.expiration_date))}` : `Expires ${new Intl.DateTimeFormat("en", { dateStyle: "long" }).format(new Date(quota.expiration_date))}`) : (isArabic ? "لا يوجد تاريخ انتهاء متاح" : "No expiration date available")}</p>
        </div>
      </section>
    </div>
  );
}
